// Contracts for «Dragon Gate» (carp, 鲤鱼跃龙门): a single-player ladder game on the user's seed pair.
//   GET  /api/carp/state                              → CarpStateResponse
//   POST /api/carp/start   body CarpStartInput         → CarpActionResponse
//   POST /api/carp/step    body CarpStepInput          → CarpActionResponse
//   POST /api/carp/cashout body CarpCashoutInput       → CarpActionResponse
import { z } from 'zod';
import { zAmount, zIdempotencyKey } from '../common';
import type { FairProof } from '../fair';

export const CARP_MODES = ['easy', 'medium', 'hard'] as const;
export type CarpMode = (typeof CARP_MODES)[number];
export const CARP_STEPS = 8;

export const zCarpStartInput = z.object({ bet: zAmount, mode: z.enum(CARP_MODES), idempotencyKey: zIdempotencyKey }).strict();
export const zCarpStepInput = z
  .object({ gameId: z.string().min(1).max(64), step: z.number().int().min(1).max(CARP_STEPS), cell: z.number().int().min(0).max(2) })
  .strict();
export const zCarpCashoutInput = z.object({ gameId: z.string().min(1).max(64) }).strict();
export type CarpStartInput = z.infer<typeof zCarpStartInput>;
export type CarpStepInput = z.infer<typeof zCarpStepInput>;
export type CarpCashoutInput = z.infer<typeof zCarpCashoutInput>;

export type CarpStatus = 'active' | 'lost' | 'cashed';

export interface CarpGameView {
  gameId: string;
  betId: string;
  mode: CarpMode;
  bet: number;
  /** steps passed, 0…8 */
  step: number;
  /** chosen cell on every passed / lost step */
  picks: number[];
  status: CarpStatus;
  /** current multiplier (0 before the first step) */
  multiplier: number;
  /** final payout (0 while active or after a loss) */
  payout: number;
  /** rocks on steps 1…8 — only when status !== 'active' */
  layout: number[][] | null;
}

/** multipliers[k-1] = m_k */
export interface CarpModeInfo {
  cells: number;
  rocks: number;
  multipliers: number[];
}

export interface CarpStateResponse {
  game: CarpGameView | null;
  fair: { serverSeedHash: string; clientSeed: string; nonce: number };
  modes: Record<CarpMode, CarpModeInfo>;
  minBet: number;
  maxBet: number;
  maxWin: number;
}

export interface CarpActionResponse {
  game: CarpGameView;
  balance: number;
  /** serverSeed = null until the seed is rotated */
  fair: FairProof;
  duplicate: boolean;
}

export interface CarpBetContext {
  mode: CarpMode;
  picks: number[];
  layout: number[][] | null;
}
